import React from "react";
import { translateService, dateHelperService, signService } from "../../services/imports";
import { saveBlob } from "../../services/signService";
import shareService from "../../services/shareService";
import EvidenceViewer from "./evidenceViewer";

// re-read while something is under way: a process not ended, or the
// document held - and only while the tab is visible
const POLL_MS = 30 * 1000;
// a focus and a visibility change come together: one read for both
const REREAD_GAP_MS = 2000;
const GROUPS = ["inProgress", "signed", "failed", "expired", "cancelled"];
// SIGNING_FAILED's failure codes the owner reads as one: the share changed
const SHARE_FAILURES = ["SHARE_WITHDRAWN", "GRANT_ENDED", "SIGN_NOT_GRANTED"];
const KNOWN_FAILURES = ["DOCUMENT_CHANGED", "VERSION_NOT_STORED", "SIGNER_UNAVAILABLE"];

const t = (k, ...a) => translateService.t(`processes.${k}`, ...a);
const date = (ms) => (ms ? dateHelperService.format(ms, dateHelperService.smallDateTimeFormat) : "");

// a process whose signing vsys stored is signed, whatever its gateway status
const groupOf = (p, signedIds) => {
  if (signedIds.has(p.processId) || p.status === "SIGNED") return "signed";
  if (!p.ended) return "inProgress";
  if (p.status === "EXPIRED") return "expired";
  if (p.status === "CANCELLED") return "cancelled";
  // SIGNING_FAILED, SIGNATORY_MISMATCH, VERIFICATION_UNDECIDED, and a
  // VERIFICATION_REJECTED or VERIFICATION_EXPIRED with no attempt left
  return "failed";
};

// evidence kinds the viewer shows in the page; the rest (pdf) are downloaded
const VIEWABLE = ["photo", "video"];

// the owner's text for a failed process: by status, for SIGNING_FAILED by its
// failure code (its message is the signatory's, never shown)
const failureText = (p) => {
  if (p.status !== "SIGNING_FAILED") return t(`status.${p.status}`) || t("failure.generic");
  const code = p.failure && p.failure.code;
  if (SHARE_FAILURES.includes(code)) return t("failure.share");
  return KNOWN_FAILURES.includes(code) ? t(`failure.${code}`) : t("failure.generic");
};

// Signing attempts (vsys docs/signings-runbook.md §4): the document's signing
// processes from signature-gateway's listing - whoever the caller may see
// them for (the uploader all, a sharer through their shares) - in groups,
// with "Someone is signing this document now" while one holds the document.
// Read on mount and when the document changes, again on tab focus, every
// 30 s while something is under way, and on "Refresh".
//
// props: docId (exposed id), signedIds (the processIds of /signings: vsys
// wins), canListShares (a starter's address comes from the share list),
// paused (the page is hidden: no polling, no re-read on focus)
export default class SigningAttempts extends React.Component {
  state = {
    list: null, // { beingSigned, processes }
    loading: false,
    error: false,
    addresses: {}, // shareId -> the recipient's address
    open: { inProgress: true },
    downloading: null, // `${processId}:${kind}` of the evidence being fetched
    evidenceErrors: {}, // processId -> why its evidence could not be fetched
    viewing: null, // { processId, kind, caption } of the evidence in the viewer
  };

  componentDidMount() {
    this.load();
    this.timer = setInterval(this.poll, POLL_MS);
    window.addEventListener("focus", this.reread);
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  componentDidUpdate(prev) {
    if (prev.docId !== this.props.docId) {
      this.addressesFor = null;
      this.setState({ list: null, error: false, addresses: {} });
      this.load();
    }
  }

  componentWillUnmount() {
    this.closed = true;
    clearInterval(this.timer);
    window.removeEventListener("focus", this.reread);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  // ---- reading -----------------------------------------------------------------

  // every page of the listing (pageInfo.next as the cursor until null); an
  // answer a newer read replaced is dropped
  load = async () => {
    const docId = this.props.docId;
    if (!docId) return;
    const seq = (this.request = (this.request || 0) + 1);
    this.lastRead = Date.now();
    this.setState({ loading: true });
    try {
      let beingSigned = null;
      const processes = [];
      let cursor = null;
      do {
        const res = await signService.getDocumentProcesses(docId, cursor).toPromise();
        const page = res.data || {};
        if (!cursor) beingSigned = page.beingSigned || null;
        processes.push(...(page.processes || []));
        cursor = (page.pageInfo && page.pageInfo.next) || null;
      } while (cursor && !this.closed && seq === this.request);
      if (this.closed || seq !== this.request) return;
      this.setState({ list: { beingSigned, processes }, loading: false, error: false });
      this.loadAddresses(processes);
    } catch (e) {
      if (this.closed || seq !== this.request) return;
      this.setState({ loading: false, error: true });
    }
  };

  // under way: the document held, or a process not ended yet
  active = () => {
    const list = this.state.list;
    return !!list && (!!list.beingSigned || list.processes.some((p) => !p.ended));
  };

  poll = () => {
    if (!document.hidden && !this.props.paused && !this.state.loading && this.active()) this.load();
  };

  reread = () => {
    if (document.hidden || this.props.paused || this.state.loading) return;
    if (this.lastRead && Date.now() - this.lastRead < REREAD_GAP_MS) return;
    this.load();
  };

  onVisibility = () => !document.hidden && this.reread();

  // The addresses of the shares a process without a requester's name went
  // through (a share's link carries none), from the share list - history
  // included, since a withdrawn share keeps its processes - page by page
  // until each is found. Read once per document; the share list is the
  // sharer's or the owner's (actionRights.shareList).
  loadAddresses = async (processes) => {
    if (!this.props.canListShares) return;
    const wanted = new Set(
      processes.filter((p) => !p.requesterName && p.shareId != null && !this.state.addresses[p.shareId]).map((p) => p.shareId)
    );
    if (!wanted.size || this.addressesFor === this.props.docId) return;
    const docId = this.props.docId;
    this.addressesFor = docId;
    const addresses = {};
    try {
      let cursor = null;
      do {
        const res = await shareService.list(docId, cursor ? { cursor } : { all: true, pageSize: 100 }).toPromise();
        const page = res.data || {};
        (page.shares || []).forEach((s) => {
          addresses[s.shareId] = s.email;
          wanted.delete(s.shareId);
        });
        cursor = (page.pageInfo && page.pageInfo.next) || null;
      } while (cursor && wanted.size && !this.closed);
    } catch (e) {
      // without the addresses a row names no starter
    }
    if (!this.closed && this.props.docId === docId) this.setState((s) => ({ addresses: { ...s.addresses, ...addresses } }));
  };

  // ---- rendering ---------------------------------------------------------------

  // started by: the requester's name, else the share's address, else a
  // member of the workspace (no share); nothing for a share not found
  starter = (p) => {
    if (p.requesterName) return t("startedBy", p.requesterName);
    if (p.shareId == null) return t("startedByMember");
    const address = this.state.addresses[p.shareId];
    return address ? t("startedBy", address) : null;
  };

  // A signing's verification evidence, for a dispute: the files the gateway
  // says the caller may download now (verificationEvidenceDownloads - off
  // unless validate.ge switched the document's downloads on). The photo and
  // the video open in the viewer; the session PDF is saved as a file.
  openEvidence = (p, kind) => {
    if (!VIEWABLE.includes(kind)) return this.downloadEvidence(p, kind);
    const caption = [date(p.createdAt), this.starter(p)].filter(Boolean).join(" · ");
    this.setState((s) => ({
      viewing: { processId: p.processId, kind, caption },
      evidenceErrors: { ...s.evidenceErrors, [p.processId]: null },
    }));
  };

  downloadEvidence = (p, kind) => {
    const key = `${p.processId}:${kind}`;
    if (this.state.downloading) return;
    this.setState((s) => ({ downloading: key, evidenceErrors: { ...s.evidenceErrors, [p.processId]: null } }));
    signService.getVerificationEvidence(p.processId, kind).subscribe(
      (file) => {
        saveBlob(file.blob, file.name);
        if (!this.closed) this.setState({ downloading: null });
      },
      (e) =>
        !this.closed &&
        this.setState((s) => ({
          downloading: null,
          evidenceErrors: { ...s.evidenceErrors, [p.processId]: signService.problemMessage(e, t("evidence.error")) },
        }))
    );
  };

  renderEvidence(p) {
    const kinds = p.verificationEvidenceDownloads || [];
    if (!kinds.length) return null;
    const error = this.state.evidenceErrors[p.processId];
    return (
      <span className="signing-attempts__evidence">
        <span>{t("evidence.label")}</span>
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            className="signing-attempts__download"
            disabled={!!this.state.downloading}
            onClick={() => this.openEvidence(p, kind)}
          >
            {this.state.downloading === `${p.processId}:${kind}` ? "…" : t(`evidence.${kind}`) || kind}
          </button>
        ))}
        {error && <span className="signing-attempts__reason">{error}</span>}
      </span>
    );
  }

  renderProcess = (p, group) => {
    const starter = this.starter(p);
    return (
      <li key={p.processId} className="signing-attempts__row">
        <span className="signing-attempts__date">{date(p.createdAt)}</span>
        {starter && <span className="signing-attempts__starter">{starter}</span>}
        {group === "failed" && <span className="signing-attempts__reason">{failureText(p)}</span>}
        {this.renderEvidence(p)}
      </li>
    );
  };

  renderGroups(list) {
    const signedIds = new Set(this.props.signedIds || []);
    const groups = {};
    list.processes.forEach((p) => {
      const g = groupOf(p, signedIds);
      (groups[g] = groups[g] || []).push(p);
    });
    return GROUPS.filter((g) => groups[g]).map((g) => {
      const open = !!this.state.open[g];
      const id = `signing-attempts-${g}`;
      return (
        <div key={g} className={`signing-attempts__group group-${g}`}>
          <button
            type="button"
            className="signing-attempts__group-toggle"
            aria-expanded={open}
            aria-controls={id}
            onClick={() => this.setState((s) => ({ open: { ...s.open, [g]: !s.open[g] } }))}
          >
            <span>{t(g)}</span>
            <span className="signing-attempts__count">{groups[g].length}</span>
            <span aria-hidden="true">{open ? "−" : "+"}</span>
          </button>
          {open && (
            <ul id={id} className="signing-attempts__list">
              {groups[g].map((p) => this.renderProcess(p, g))}
            </ul>
          )}
        </div>
      );
    });
  }

  render() {
    const { list, loading, error, viewing } = this.state;
    return (
      <section className={`signing-attempts ${translateService.key}`} aria-labelledby="signing-attempts-title">
        <div className="signing-attempts__head">
          <h3 id="signing-attempts-title" className="signing-attempts__title">
            {t("title")}
          </h3>
          <button type="button" className="signing-attempts__refresh" disabled={loading} onClick={this.load}>
            {t("refresh")}
          </button>
        </div>
        {list && list.beingSigned && (
          <p className="signing-attempts__banner" role="status">
            {t("beingSigned")}
          </p>
        )}
        {error && (
          <p className="signing-attempts__error" role="alert">
            {t("loadError")}
          </p>
        )}
        {list && !list.processes.length && <p className="signing-attempts__none">{t("none")}</p>}
        {list && this.renderGroups(list)}
        {!list && loading && <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />}
        {viewing && (
          <EvidenceViewer
            key={`${viewing.processId}:${viewing.kind}`}
            processId={viewing.processId}
            kind={viewing.kind}
            caption={viewing.caption}
            onClose={() => this.setState({ viewing: null })}
          />
        )}
      </section>
    );
  }
}
